import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, X, Loader2, Sparkles, User, Lock, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * AICoachGuestChat — Bouton flottant + chat public limité (3 questions / 24h par IP)
 * pour la page de résultats du calculateur. Booste la conversion en créant
 * un moment "wow" entre la fin du calcul et la décision d'achat.
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PROACTIVE_DELAY_MS = 30000; // 30s avant la bulle proactive

// Construit une question contextuelle basée sur le calcul
const buildProactiveQuestion = (ctx = {}, en = false) => {
  const goal = (ctx.goal || '').toLowerCase();
  const protein = ctx.protein_g;

  if (goal.includes('perte') || goal.includes('loss') || goal.includes('weight')) {
    return en
      ? `Show me a sample 1-day menu to hit ${protein || 'my'}g protein on a deficit 🍳`
      : `Montre-moi un menu type 1 jour pour atteindre ${protein ? protein + 'g de' : 'mes'} protéines en déficit 🍳`;
  }
  if (goal.includes('muscle') || goal.includes('prise') || goal.includes('gain') || goal.includes('bulk')) {
    return en
      ? `What should I eat post-workout to maximize muscle gain? 💪`
      : `Que manger après ma séance pour maximiser la prise de muscle ? 💪`;
  }
  if (goal.includes('maint') || goal.includes('recomp')) {
    return en
      ? `How do I split my macros across my meals for the day? 🍽️`
      : `Comment je répartis mes macros sur mes repas dans la journée ? 🍽️`;
  }
  return en
    ? `Give me 3 quick tips to make this plan stick long-term 🎯`
    : `Donne-moi 3 astuces pour tenir ce plan sur la durée 🎯`;
};

const AICoachGuestChat = ({ calcContext = {}, language = 'fr' }) => {
  const en = language === 'en';
  const L = (fr, eng) => (en ? eng : fr);

  const intro = L(
    "👋 Salut ! Je suis Laurent, coach Body-Up. Tu viens de finir ton calcul — pose-moi 3 questions GRATUITES sur ta nutrition, tes macros ou tes objectifs 💪",
    "👋 Hi! I'm Laurent, your Body-Up coach. You just finished your calculation — ask me 3 FREE questions about nutrition, macros or your goals 💪"
  );

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', content: intro }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [remaining, setRemaining] = useState(3);
  const [limitReached, setLimitReached] = useState(false);
  const [proactiveOpen, setProactiveOpen] = useState(false);
  const [proactiveDismissed, setProactiveDismissed] = useState(false);
  const [leadEmail, setLeadEmail] = useState('');
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [leadCaptureDismissed, setLeadCaptureDismissed] = useState(false);
  const proactiveQuestion = buildProactiveQuestion(calcContext, en);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const interactedRef = useRef(false);
  // Ref to always have the latest messages without stale-closure in sendMessage
  const messagesRef = useRef(messages);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Keep messagesRef in sync
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 200);
  }, [isOpen]);

  // Déclenche la bulle proactive 30s après le mount,
  // uniquement si l'utilisateur n'a pas déjà interagi avec le chat.
  useEffect(() => {
    const t = setTimeout(() => {
      if (!interactedRef.current && !proactiveDismissed) {
        setProactiveOpen(true);
      }
    }, PROACTIVE_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const sendMessage = useCallback(async (rawText) => {
    const txt = (rawText || '').trim();
    if (!txt || loading || limitReached) return;
    if (txt.length > 500) {
      toast.error(L('Message trop long (500 max).', 'Message too long (500 max).'));
      return;
    }

    interactedRef.current = true;
    setProactiveOpen(false);

    // Build nextMessages synchronously using the ref (always up-to-date, avoids
    // React 19 concurrent-mode issue where functional updater side-effects are not
    // guaranteed to run before the next line of async code).
    const nextMessages = [...messagesRef.current, { role: 'user', content: txt }];
    setMessages(nextMessages);
    setLoading(true);

    try {
      const res = await fetch(`${BACKEND}/api/ai-coach/chat-guest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: nextMessages.map(m => ({ role: m.role, content: m.content })),
          calc_context: calcContext,
        }),
      });
      const data = await res.json();

      if (data.limit_reached) {
        setLimitReached(true);
        setRemaining(0);
        setMessages([...nextMessages, { role: 'assistant', content: data.message, isLimit: true }]);
      } else if (data.ok) {
        setRemaining(typeof data.remaining === 'number' ? data.remaining : (r) => Math.max(0, r - 1));
        setMessages([...nextMessages, { role: 'assistant', content: data.message }]);
      } else {
        throw new Error(data.detail || 'API error');
      }
    } catch (err) {
      console.error('Guest coach error:', err);
      toast.error(L('Erreur de connexion. Réessaye.', 'Connection error. Try again.'));
      // rollback: retirer le dernier message user
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  }, [loading, limitReached, calcContext, en]);

  const handleSend = () => {
    const txt = input.trim();
    if (!txt) return;
    setInput('');
    sendMessage(txt);
  };

  const handleLeadCapture = async (e) => {
    e.preventDefault();
    if (!leadEmail.trim() || leadSubmitting) return;
    setLeadSubmitting(true);
    try {
      const chatSummary = messages.slice(-8).map(m =>
        `${m.role === 'user' ? L('Visiteur', 'Visitor') : 'Laurent'}: ${m.content}`
      ).join('\n');
      const res = await fetch(`${BACKEND}/api/ai-coach/capture-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: leadEmail.trim(),
          chat_summary: chatSummary,
          calc_context: calcContext,
          language,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setLeadSubmitted(true);
        toast.success(L('Super ! On te recontacte très bientôt 🎉', 'Great! We\'ll reach out to you soon 🎉'));
      } else {
        throw new Error(data.detail || 'Error');
      }
    } catch (err) {
      console.error('Lead capture error:', err);
      toast.error(L('Erreur. Réessaye.', 'Error. Try again.'));
    } finally {
      setLeadSubmitting(false);
    }
  };

  // Click sur la bulle proactive : ouvre le chat + envoie auto la question
  const acceptProactive = () => {
    interactedRef.current = true;
    setProactiveOpen(false);
    setIsOpen(true);
    // Laisser le temps à l'UI de s'ouvrir avant d'envoyer
    setTimeout(() => sendMessage(proactiveQuestion), 250);
  };

  const dismissProactive = (e) => {
    e?.stopPropagation();
    setProactiveDismissed(true);
    setProactiveOpen(false);
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const userMsgCount = messages.filter(m => m.role === 'user').length;
  const showLeadCTA = userMsgCount >= 2 && !leadSubmitted && !leadCaptureDismissed && !limitReached;

  return (
    <>
      {/* Floating Button + Proactive Bubble */}
      {!isOpen && (
        <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3">
          {/* Bulle proactive (apparaît après 30s) */}
          <AnimatePresence>
            {proactiveOpen && !limitReached && (
              <motion.div
                initial={{ opacity: 0, y: 12, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.95 }}
                transition={{ type: 'spring', damping: 20 }}
                className="relative max-w-[280px] sm:max-w-[320px]"
                data-testid="ai-coach-guest-proactive"
              >
                <button
                  type="button"
                  onClick={acceptProactive}
                  className="block text-left w-full bg-gradient-to-br from-zinc-900 to-black border border-primary/40 rounded-2xl p-4 pr-8 shadow-2xl shadow-primary/30 hover:border-primary/70 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center shrink-0">
                      <Sparkles className="w-3.5 h-3.5 text-white" />
                    </div>
                    <span className="text-[11px] font-bold text-primary uppercase tracking-wider">
                      {L('Coach Laurent', 'Coach Laurent')}
                    </span>
                  </div>
                  <p className="text-sm text-white leading-snug font-medium">{proactiveQuestion}</p>
                  <p className="text-[11px] text-primary mt-2 font-semibold">
                    {L('👉 Touche pour la réponse', '👉 Tap to get the answer')}
                  </p>
                </button>
                <button
                  type="button"
                  onClick={dismissProactive}
                  className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 text-zinc-400 hover:text-white flex items-center justify-center transition-colors"
                  data-testid="ai-coach-guest-proactive-dismiss"
                  aria-label="Dismiss"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
                {/* Pointeur vers le bouton */}
                <div className="absolute -bottom-1.5 right-8 w-3 h-3 rotate-45 bg-black border-r border-b border-primary/40" />
              </motion.div>
            )}
          </AnimatePresence>

          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.6, type: 'spring' }}
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              interactedRef.current = true;
              setProactiveOpen(false);
              setIsOpen(true);
            }}
            className="group"
            data-testid="ai-coach-guest-button"
          >
            <div className="relative flex items-center gap-3 pl-4 pr-5 py-3 rounded-full bg-gradient-to-r from-primary via-pink-500 to-purple-500 text-white shadow-2xl shadow-primary/40">
              <div className="relative w-10 h-10 rounded-full bg-white/15 flex items-center justify-center backdrop-blur-sm">
                <Sparkles className="w-5 h-5" />
                <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-pink-500" />
              </div>
              <div className="text-left hidden sm:block">
                <div className="text-sm font-bold leading-tight">{L('Demande à Laurent', 'Ask Laurent')}</div>
                <div className="text-[10px] uppercase tracking-wider opacity-90">{L('3 questions gratuites', '3 free questions')}</div>
              </div>
              {!proactiveOpen && (
                <span className="absolute inset-0 rounded-full bg-white/20 animate-ping opacity-50 -z-10" />
              )}
            </div>
          </motion.button>
        </div>
      )}

      {/* Chat window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-6 right-6 z-50 w-[400px] max-w-[calc(100vw-24px)] h-[600px] max-h-[calc(100vh-80px)] bg-gradient-to-br from-zinc-900 to-black border border-white/10 rounded-3xl shadow-2xl overflow-hidden flex flex-col"
            data-testid="ai-coach-guest-chat"
          >
            {/* Header */}
            <div className="relative px-5 py-4 bg-gradient-to-r from-primary/20 to-purple-500/20 border-b border-white/10">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,23,68,0.18),transparent_50%)]" />
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-zinc-900" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white text-sm">{L('Coach Laurent', 'Coach Laurent')}</h3>
                    <p className="text-[11px] text-zinc-400">
                      {limitReached
                        ? L('Limite quotidienne atteinte', 'Daily limit reached')
                        : L(`${remaining} question${remaining > 1 ? 's' : ''} gratuite${remaining > 1 ? 's' : ''} restante${remaining > 1 ? 's' : ''}`,
                            `${remaining} free question${remaining > 1 ? 's' : ''} left`)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-lg hover:bg-white/10 text-zinc-400 hover:text-white transition-colors"
                  data-testid="ai-coach-guest-close"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-br from-blue-500 to-cyan-500'
                      : 'bg-gradient-to-br from-primary to-pink-500'
                  }`}>
                    {msg.role === 'user' ? <User className="w-4 h-4 text-white" /> : <Sparkles className="w-4 h-4 text-white" />}
                  </div>
                  <div className={`max-w-[78%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 text-white'
                      : msg.isLimit
                        ? 'bg-gradient-to-br from-primary/20 to-pink-500/20 border border-primary/40 text-white'
                        : 'bg-zinc-800/50 border border-white/10 text-zinc-100'
                  }`}>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                    {msg.isLimit && (
                      <div className="mt-3 flex flex-col gap-2">
                        <Link
                          to="/calculateur-calories?showCheckout=meal_plan"
                          className="block text-center text-xs font-semibold py-2 px-3 rounded-lg bg-gradient-to-r from-primary to-pink-500 text-white hover:scale-[1.02] transition-transform"
                          data-testid="ai-coach-guest-cta-mealplan"
                        >
                          {L('Plan Nutrition Complet — 17 CHF →', 'Full Nutrition Plan — 17 CHF →')}
                        </Link>
                        <Link
                          to="/espace-client"
                          className="block text-center text-xs font-semibold py-2 px-3 rounded-lg bg-white/5 border border-white/15 text-white hover:bg-white/10 transition-colors"
                          data-testid="ai-coach-guest-cta-signup"
                        >
                          {L('Créer un compte gratuit', 'Create a free account')}
                        </Link>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}

              {loading && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <div className="bg-zinc-800/50 border border-white/10 rounded-2xl px-4 py-3">
                    <Loader2 className="w-5 h-5 text-primary animate-spin" />
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input or upsell */}
            <div className="border-t border-white/10 bg-zinc-900/50">
              {/* Lead capture CTA — apparaît après 2 échanges */}
              <AnimatePresence>
                {showLeadCTA && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.25 }}
                    className="px-3 pt-3 overflow-hidden"
                    data-testid="ai-coach-lead-capture"
                  >
                    <div className="bg-gradient-to-r from-primary/10 to-purple-500/10 border border-primary/25 rounded-xl p-3">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-primary shrink-0" />
                          <p className="text-xs text-white font-semibold leading-snug">
                            {L('Reçois ce chat + réserve un appel gratuit', 'Get this chat + book a free call')}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setLeadCaptureDismissed(true)}
                          className="text-zinc-500 hover:text-zinc-300 transition-colors shrink-0"
                          data-testid="ai-coach-lead-dismiss"
                          aria-label="Fermer"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <form onSubmit={handleLeadCapture} className="flex gap-2">
                        <input
                          type="email"
                          value={leadEmail}
                          onChange={(e) => setLeadEmail(e.target.value)}
                          placeholder={L('ton@email.com', 'your@email.com')}
                          className="flex-1 bg-zinc-800/80 border border-white/10 rounded-lg px-3 py-2 text-white text-xs placeholder-zinc-500 focus:border-primary focus:outline-none"
                          required
                          data-testid="ai-coach-lead-email-input"
                        />
                        <button
                          type="submit"
                          disabled={leadSubmitting || !leadEmail.trim()}
                          className="shrink-0 px-3 py-2 rounded-lg bg-gradient-to-r from-primary to-pink-500 text-white text-xs font-semibold hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="ai-coach-lead-submit"
                        >
                          {leadSubmitting
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : L('Envoyer', 'Send')}
                        </button>
                      </form>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="p-3">
              {limitReached ? (
                <div className="flex items-center justify-center gap-2 py-2 text-xs text-zinc-400">
                  <Lock className="w-3.5 h-3.5" />
                  {L('Reviens demain ou débloque l\'accès illimité', 'Come back tomorrow or unlock unlimited access')}
                </div>
              ) : (
                <>
                  <div className="flex items-end gap-2">
                    <textarea
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyPress={onKey}
                      placeholder={L('Ex: Comment atteindre ces macros ?', 'Ex: How do I hit these macros?')}
                      className="flex-1 bg-zinc-800 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-zinc-500 focus:border-primary focus:outline-none resize-none"
                      rows={1}
                      style={{ minHeight: '44px', maxHeight: '100px' }}
                      disabled={loading}
                      maxLength={500}
                      data-testid="ai-coach-guest-input"
                    />
                    <button
                      onClick={handleSend}
                      disabled={loading || !input.trim()}
                      className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-r from-primary to-pink-500 text-white flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="ai-coach-guest-send"
                      aria-label="Send"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-[10px] text-zinc-500 mt-2 text-center">
                    {L('Conseils IA personnalisés • Propulsé par GPT', 'AI-powered personalized advice • Powered by GPT')}
                  </p>
                </>
              )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AICoachGuestChat;
